import { useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../../../redux/store';
import { formatDate } from '../../../service/utils';
import { Prompt } from '../../../interface/Prompt';
import {
    audioPlayReasons,
    frameMappingData,
    audioEndReasons as promptEndReasons,
} from '../../../constants/constants';
import { formatDuration, checkForHeadset } from '../../../service/MicService';
import { saveSpeakingTestProgressionData } from '../../../api/authApisV2';

interface PlayedAudio {
    audioFileURL: string;
    audioFilePlayDateTime: string;
    audioFilePlayReason: string;
    testTimeElapsed: string | number;
    intent?: string;
    transcript?: string;
    lexSessionId?: string;
    confidenceScore?: string | null;
    selectedMicrophone?: string;
    //
    promptTimeSpent?: string | number;
    audioFileEndReason?: string;
    //
    responseFilePath?: string;
    activeMicrophone?: string;
    micChanged?: boolean;
}

interface FrameData {
    frame: string | undefined;
    frameTimeSpent: string | number;
    frameDetails: PlayedAudio[];
    frameType?: string;
    startTimestamp?: string;
    reasonForEnd?: string;
}

interface Options {
    currentPrompt: Prompt;
    nextPrompt: Prompt;
}

interface PartSummary {
    PART_1: PartData;
    PART_2: PartData;
    PART_3: PartData;
}

interface PartData {
    startTimestamp: string;
    timeSpent: string | number;
    reasonForEnd: string;
}

export const useTestProgression = ({ currentPrompt, nextPrompt }: Options) => {
    const frameStartRef = useRef(0);
    const frameIndexRef = useRef(0);
    const partStartTime = useRef(0);
    const playDateTimeRef = useRef('');
    const currentPromptStartTimeRef = useRef(0);
    const deviceChangedRef = useRef(false);
    const playedAudiosRef = useRef<PlayedAudio[]>([]);
    const frameAudiosRef = useRef<FrameData[]>([]);
    const logIdRef = useRef('');
    const forceUploadRef = useRef(false);

    const [testStartTime, setTestStartTime] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [timings, setTimings] = useState<PartSummary>({
        PART_1: {
            startTimestamp: '',
            timeSpent: '',
            reasonForEnd: '',
        },
        PART_2: { startTimestamp: '', timeSpent: '', reasonForEnd: '' },
        PART_3: { startTimestamp: '', timeSpent: '', reasonForEnd: '' },
    });

    const { selectedMic, testTaker, insperaDetails, appFontSize, appTheme, fileSpeed, speakCheckScreenDetails } =
        useSelector((state: RootState) => state.appConfigSlice);

    const markTestStartTime = () => setTestStartTime(Date.now());

    //run on audio play
    const logProgressionData = (audioFileUrl: string, audioFilePlayReason?: string, isPrompt?: boolean) => {
        const audioStartTime = Date.now();
        const elapsed = audioStartTime - testStartTime;
        const timeElapsed = formatDuration(elapsed);

        if (isPrompt) currentPromptStartTimeRef.current = audioStartTime;

        playedAudiosRef.current.push({
            audioFileURL: audioFileUrl,
            audioFilePlayDateTime: formatDate(new Date()),
            audioFilePlayReason: audioFilePlayReason ?? audioPlayReasons.QUESTION_COMPLETED,
            selectedMicrophone: selectedMic?.label,
            testTimeElapsed: timeElapsed,
            intent: '',
            transcript: '',
            lexSessionId: 'N/A',
            confidenceScore: null,
        });
    };

    //run when currentPrompt is changed
    const logPromptEndData = async (promptEndReason: keyof typeof promptEndReasons, forceUpload: boolean) => {
        const currentAudio = playedAudiosRef.current[playedAudiosRef.current.length - 1];

        if (!currentAudio) return;

        playDateTimeRef.current = currentAudio.audioFilePlayDateTime;
        forceUploadRef.current = forceUpload;

        const promptEndTime = Date.now();
        const elapsed = promptEndTime - currentPromptStartTimeRef.current;
        const timeSpent = formatDuration(elapsed, true);

        let selectedMp: any = selectedMic;

        if (deviceChangedRef.current || !selectedMp.deviceId) {
            const allDevices = await window.navigator.mediaDevices.enumerateDevices();
            const micList = allDevices.filter((d) => d.kind === 'audioinput' && /^[0-9a-f]+$/i.test(d.deviceId));
            selectedMp = checkForHeadset(micList);
            selectedMp ??= { deviceId: micList[0].deviceId, label: micList[0].label, groupId: micList[0].groupId };
        }

        currentAudio.activeMicrophone = selectedMp;
        currentAudio.micChanged = deviceChangedRef.current;
        currentAudio.promptTimeSpent = timeSpent;
        currentAudio.audioFileEndReason = promptEndReasons[promptEndReason];

        deviceChangedRef.current = false;
    };

    //run on every after partial audio upload
    const updateResponseFilePath = (responseFilePath: string) => {
        const currentAudio = playedAudiosRef.current.find(
            (playedAudio) => playedAudio.audioFilePlayDateTime === playDateTimeRef.current,
        );

        if (currentAudio) currentAudio.responseFilePath = responseFilePath;
    };

    const uploadTestProgression = async (fullRecordingPath?: string) => {
        if (nextPrompt?.frame === currentPrompt?.frame && !forceUploadRef.current && !fullRecordingPath) return;

        const frameEndtTime = Date.now();
        const elapsed = frameEndtTime - (frameStartRef.current || testStartTime);
        const timeFrame = formatDuration(elapsed, true);
        frameStartRef.current = Date.now();

        if (currentPrompt?.frame && !fullRecordingPath) {
            const isQuestion = !!currentPrompt?.expectedMaxDuration;
            const refIdx = isQuestion
                ? playedAudiosRef.current.findIndex(
                      (playedAudio) => playedAudio.audioFilePlayDateTime === playDateTimeRef.current,
                  ) + 1
                : playedAudiosRef.current.length;

            frameAudiosRef.current.push({
                frame: currentPrompt.frameType ? currentPrompt.frameType : getFrameName(currentPrompt.frame),
                frameTimeSpent: timeFrame,
                startTimestamp: formatDate(new Date(frameStartRef.current)),
                frameDetails: playedAudiosRef.current.slice(frameIndexRef.current, refIdx),
                reasonForEnd: 'Frame Completed',
            });
            frameIndexRef.current = refIdx;
        }

        const partTimings = timings;

        let description = '';
        if (fullRecordingPath) {
            partTimings['PART_3'].reasonForEnd = 'Test Completed';
            partTimings['PART_3'].timeSpent = formatDuration(Date.now() - partStartTime.current, true);
            description = 'Test Completed';
        } else if (playedAudiosRef.current.length) {
            description = `${currentPrompt.frameType ? currentPrompt.frameType : getFrameName(currentPrompt.frame)} Completed`;
        } else {
            description = 'Test Started';
        }

        forceUploadRef.current = false;

        try {
            const res = await saveSpeakingTestProgressionData({
                testTakerDetails: testTaker,
                playedAudioFilesDetails: frameAudiosRef.current,
                status: description,
                fullRecordingFilePath: fullRecordingPath ?? '',
                logId: logIdRef.current,
                partSummary: {
                    ...timings
                },
                insperaDetails: {
                    assessmentId: insperaDetails.assessmentId,
                    externalId: insperaDetails.externalId,
                    partner: insperaDetails.partner,
                    product: insperaDetails.product,
                },
                speedDetails: {
                    uploadSpeed: fileSpeed?.uploadSpeed,
                    downloadSpeed: fileSpeed?.downloadSpeed,
                },
                settings: {
                    fontSize: appFontSize,
                    colourContrast: appTheme,
                },
                precheckDetails: {
                    speakCheckDetails: speakCheckScreenDetails,
                    selectedMicLabel: selectedMic?.label,
                },
            });

            console.log('Progression data submitted successfully.', res);

            if (!fullRecordingPath) {
                //usig Regex getting the UUID from res
                const uuidRegex = /([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i;
                const uuid = res?.data.match(uuidRegex)?.[0];
                logIdRef.current = uuid;
            }
        } catch (error) {
            console.error('Error while saving progression test data', error);
            setError((error as Error).message ?? 'Error while saving progression test data');
        }
    };

    const getFrameName = (frame: string) => {
        const mapping = frameMappingData();
        const frameObj = mapping.find((item) => item.key === frame);

        return frameObj ? frameObj.value : '';
    };

    //run on every intent detection data received
    const updateIntentData = (data: any) => {
        const currentAudio = playedAudiosRef.current[playedAudiosRef.current.length - 1];
        if (!currentAudio) return;

        currentAudio.intent = data.intent;
        currentAudio.transcript = data.inputTranscript;
        currentAudio.confidenceScore = data.nluConfidenceScore;
        currentAudio.lexSessionId = data.lexSessionId;
    };

    useEffect(() => {
        window.navigator.mediaDevices.addEventListener('devicechange', async () => {
            deviceChangedRef.current = true;
            console.log('------Device changed-------');
        });

        return () => {
            window.navigator.mediaDevices.removeEventListener('devicechange', () => {
                deviceChangedRef.current = false;
            });

            playedAudiosRef.current = [];
            frameAudiosRef.current = [];
            frameStartRef.current = 0;
            frameIndexRef.current = 0;
            playDateTimeRef.current = '';
            currentPromptStartTimeRef.current = 0;
            deviceChangedRef.current = false;
            logIdRef.current = '';
        };
    }, []);

    useEffect(() => {
        const currentTiming = timings;
        const status = 'Test Completed';
        if (currentPrompt?.partName === 'PART1') {
            currentTiming['PART_1'].startTimestamp = formatDate(new Date(Date.now()));
            setTimings(currentTiming);
            partStartTime.current = Date.now();
        }

        if (currentPrompt?.partName === 'PART2') {
            currentTiming['PART_2'].startTimestamp = formatDate(new Date(Date.now()));
            currentTiming['PART_1'].reasonForEnd = status;
            currentTiming['PART_1'].timeSpent = formatDuration(Date.now() - partStartTime.current, true);
            partStartTime.current = Date.now();
            setTimings(currentTiming);
        }

        if (currentPrompt?.partName === 'PART3') {
            currentTiming['PART_3'].startTimestamp = formatDate(new Date(Date.now()));
            currentTiming['PART_2'].reasonForEnd = status;
            currentTiming['PART_2'].timeSpent = formatDuration(Date.now() - partStartTime.current, true);
            partStartTime.current = Date.now();
            setTimings(currentTiming);
        }

    }, [currentPrompt?.partName, timings]);

    useEffect(() => {
        if (!testStartTime) return;

        uploadTestProgression();
    }, [testStartTime]);

    return {
        logProgressionData,
        logPromptEndData,
        updateResponseFilePath,
        updateIntentData,
        insperaDetails,
        markTestStartTime,
        uploadTestProgression,
        testStartTime,
        error,
    };
};
